import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useDispatch, useSelector } from 'react-redux';
import SignatureCanvas from 'react-signature-canvas';
import { useAuth0 } from '@auth0/auth0-react';
import moment from 'moment';
import { Box, IconButton, List, ListItem, Paper, Typography, Grid } from '@mui/material';

import { FProgressBar, FHButton, FHConfirmDialog } from '../../../base';
import { sessionActions } from '../../../../store';
import { getUtcOffset } from '../../../../utils/common';
import { FORTA_GRAY4, FORTA_BLUE, FORTA_WHITE } from '../../../../utils/colors';
import { history, areUserRolesIn } from '../../../../helpers';
import PageContainer from '../../PageContainer';
import ReviewInfoCard from './ReviewInfoCard';
import CriteriaCard from './CriteriaCard';
import { getLatestDiscourseComment } from './utils';
import { COMMENT_SECTION_SX } from './styles';
import { Roles, BTSessionStatus, Themes } from '../../../../constants';
import { CQAReviewStatus, SessionEvalSchemaVersion } from '../constants';
import ClearSignatureIcon from '../../../svgs/ClearSignatureIcon';
import { ScoreFieldsV1, ScoreFieldsV2 } from '../review-dialog/config';

import type { RootState } from '../../../../store';

interface ISessionReviewProps {
  isAdminView?: boolean;
}

function SessionReview({ isAdminView = false }: ISessionReviewProps) {
  const { id: sessionId } = useParams();
  const dispatch = useDispatch();
  const { getAccessTokenSilently } = useAuth0();
  const { user } = useSelector((state: RootState) => state.auth);
  const { current: session, loading: sessionLoading } = useSelector(
    (state: RootState) => state.sessions,
  );
  const [showConfirmDialog, setShowConfirmDialog] = useState<boolean>(false);
  const { session_provider, clinical_qa_review, date_start, id, status } = session || {};
  const [openConfirmDownload, setOpenConfirmDownload] = useState<boolean>(false);
  const [downloadAllowed, setDownloadAllowed] = useState<boolean>(false);
  const [signatureCanvasRef, setSignatureCanvasRef] = useState<any>(null);
  const userRole = user?.roles?.find((role: string): role is Roles =>
    Object.values(Roles).some((knownRole) => knownRole === role),
  );

  useEffect(() => {
    return () => {
      dispatch(sessionActions.clearSession());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!sessionId) return;
    dispatch(sessionActions.updateState({ key: 'clinicalReview', data: [] }));

    (async function () {
      const response = await dispatch(
        sessionActions.getSession({ sessionId, getAccessTokenSilently }),
      );

      if (response.error) {
        history.navigate('/dashboard');
        return;
      }

      const lastClinicalQAReview = response.payload?.clinical_qa_review;
      if (!lastClinicalQAReview) {
        toast.error('Session note has not been reviewed yet.');
        history.navigate('/dashboard');
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  useEffect(() => {
    if (!clinical_qa_review || !signatureCanvasRef) return;

    const { acknowledged_signature } = clinical_qa_review || {};
    if (acknowledged_signature) {
      const img = new Image();
      img.src = acknowledged_signature;
      img.onload = () => {
        // Ensure that both the image and the session are ready
        if (signatureCanvasRef && signatureCanvasRef.fromDataURL) {
          signatureCanvasRef.fromDataURL(acknowledged_signature, {
            width: img.width,
            height: img.height,
          });
          signatureCanvasRef.off();
        }
      };
    } else {
      // Handle the case where acknowledged_signature is not available
      if (signatureCanvasRef && !areUserRolesIn(user?.roles, [Roles.Prbt, Roles.Vbt])) {
        signatureCanvasRef.off();
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clinical_qa_review, signatureCanvasRef]);

  useEffect(() => {
    const downloadAllowed =
      session?.status === BTSessionStatus.Completed &&
      session?.clinical_qa_review?.status === CQAReviewStatus.Acknowledged &&
      areUserRolesIn(user?.roles, [
        Roles.Admin,
        Roles.QaSpecialist,
        Roles.ClientSuccessManager,
        Roles.Biller,
        Roles.InvoiceCoordinator,
      ]);

    setDownloadAllowed(downloadAllowed);
  }, [session, user]);

  const handleClear = () => {
    signatureCanvasRef.clear();
  };

  const handleContinue = () => {
    history.navigate(`/sessions/${sessionId}`);
  };

  const handleAcknowledgement = async () => {
    if (signatureCanvasRef?.isEmpty()) {
      toast.error('A signature is required');
      return;
    }

    const data = {
      id: clinical_qa_review?.id,
      acknowledged_signature: signatureCanvasRef.toDataURL('image/png'),
      date_review_acknowledged: moment(new Date()),
      utc_offset_date_review_acknowledged: getUtcOffset(moment(new Date())),
    };
    const response = await dispatch(
      sessionActions.acknowledgeCQAReview({
        sessionId,
        data,
        getAccessTokenSilently,
      }),
    );

    if (response.error) {
      const toastId = 'errorToast';
      toast.error(response.error?.message, { toastId });
    } else {
      const toastId = 'sessionNotAckToast';
      toast.success('Session note review acknowledged successfully', { toastId });
      history.navigate(`/dashboard`);
    }
  };

  const handleSessionNoteReviewDownload = async () => {
    if (!session) return;
    setOpenConfirmDownload(false);
    const response = await dispatch(
      sessionActions.downloadSessionNoteReview({
        sessionId: session.id,
        clinicalQAReviewId: session.clinical_qa_review?.id,
        getAccessTokenSilently,
      }),
    );
    if (response.payload?.document_url) {
      window.location.href = response.payload?.document_url;
      toast.success('Session note review downloaded');
    } else if (response.error?.message) {
      toast.error(response.error?.message);
    } else {
      toast.error('Something went wrong while downloading the pdf');
    }
  };

  const ScoreFields =
    clinical_qa_review?.eval_schema_version === SessionEvalSchemaVersion.V2
      ? ScoreFieldsV2
      : ScoreFieldsV1;

  const latestDiscourseComment = getLatestDiscourseComment(clinical_qa_review?.discourses);

  if (!session || sessionLoading) return <FProgressBar />;

  return (
    <PageContainer
      dataTestId="sessionReviewContainer"
      sx={{ padding: '16px 16px 90px 16px', gap: 2 }}
    >
      <Box
        sx={{
          backgroundColor: FORTA_BLUE.light3,
          padding: 3,
          gap: 3,
          borderRadius: '10px',
        }}
      >
        {clinical_qa_review && userRole && <ReviewInfoCard session={session} role={userRole} />}
        <Box
          sx={{
            width: '100%',
            height: '2px',
            marginTop: '24px',
            backgroundColor: FORTA_GRAY4,
          }}
        />
        <Typography
          sx={{
            fontSize: '24px',
            fontWeight: 700,
            color: FORTA_BLUE.primary,
            marginTop: 3,
            textAlign: 'left',
          }}
        >
          Session notes are reviewed for the following criteria:
        </Typography>
        <Box
          sx={{
            fontSize: '16px',
            fontWeight: 700,
            color: FORTA_BLUE.light1,
            textAlign: 'left',
            marginTop: 3,
          }}
        >
          <Typography
            sx={{
              fontSize: '16px',
              fontWeight: 700,
            }}
          >
            Attention
          </Typography>
          <List
            sx={{
              listStyleType: 'disc',
              listStylePosition: 'inside',
              p: 0,
            }}
          >
            <ListItem sx={{ display: 'list-item', py: 0 }}>
              Each criteria is equivalent to 10 points. The goal is to pass at least 8 of the 10
              requirements for a score of 80.
            </ListItem>
            <ListItem sx={{ display: 'list-item', py: 0 }}>
              Any data that has a (E) next to it indicates that the data point can be edited.
            </ListItem>
          </List>
        </Box>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            padding: '24px 0px',
          }}
        >
          <Typography sx={{ fontSize: '24px', fontWeight: '700', marginLeft: '10px' }}>
            Criteria
          </Typography>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              minWidth: '140px',
              mr: 2,
            }}
          >
            <Typography sx={{ fontSize: '24px', fontWeight: '700' }}>Status</Typography>
          </Box>
        </Box>
        {ScoreFields.map((item) => (
          <CriteriaCard
            key={item.field}
            title={item.label}
            result={
              clinical_qa_review?.[item.field] ?? null
            }
          />
        ))}
        {latestDiscourseComment && (
          <Box sx={COMMENT_SECTION_SX}>
            <Typography sx={{ fontSize: '24px', fontWeight: '700' }}>Comment:</Typography>
            <Typography sx={{ fontSize: '20px' }}>{latestDiscourseComment}</Typography>
          </Box>
        )}
        {!isAdminView && (
          <>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                padding: 2,
                alignItems: 'flex-start',
                marginTop: 3,
                backgroundColor: FORTA_WHITE,
                borderRadius: '10px',
              }}
            >
              <Typography sx={{ fontSize: '24px', fontWeight: '700' }}>Next steps:</Typography>
              <List
                sx={{
                  listStyleType: 'disc',
                  listStylePosition: 'inside',
                  mx: '-12px',
                  fontSize: '20px',
                }}
              >
                {status === BTSessionStatus.Completed && (
                  <ListItem sx={{ display: 'list-item', py: 0.5 }}>
                    You will need to sign this session note review.
                  </ListItem>
                )}
                {status === BTSessionStatus.AwaitingReview && (
                  <ListItem
                    sx={{
                      display: 'list-item',
                      py: 0.5,
                    }}
                  >
                    Your edits have been submitted.
                  </ListItem>
                )}
              </List>
              {status === BTSessionStatus.EditsRequested && (
                <List
                  sx={{
                    listStyleType: 'numbered',
                    listStylePosition: 'inside',
                    mx: '-12px',
                    fontSize: '20px',
                    [`& li.MuiListItem-root::marker`]: {
                      fontWeight: 700,
                    },
                  }}
                >
                  <ListItem sx={{ display: 'list-item', py: 0.5 }}>
                    You will need to sign this session note review.
                  </ListItem>
                  <ListItem sx={{ display: 'list-item', py: 0.5 }}>
                    Your session note {moment(date_start).format('M/D/YYYY h:mm A')} (Session ID:
                    {id}) is now in Edit mode.
                    <Typography
                      sx={{
                        fontSize: '20px',
                        fontWeight: 700,
                        margin: '8px 0px 0px 24px',
                      }}
                    >
                      Note: All edits must be completed within 48 hours.
                    </Typography>
                  </ListItem>
                  <ListItem sx={{ display: 'list-item', py: 0.5 }}>
                    Once you complete editing the session notes, it will need to be concluded and
                    signed.
                  </ListItem>
                </List>
              )}
              <FHButton
                data-testid="viewSessioNoteButton"
                variant="contained"
                sx={{
                  width: '190px',
                  marginTop: 2,
                  px: 1,
                  py: 2,
                }}
                onClick={
                  areUserRolesIn(user?.roles, [Roles.Prbt, Roles.Vbt])
                    ? () => setShowConfirmDialog(true)
                    : handleContinue
                }
              >
                View session note
              </FHButton>
              <FHConfirmDialog
                open={showConfirmDialog}
                colorTheme={Themes.Primary}
                title={'Attention'}
                content={
                  'By leaving this page, you will be redirected to the session note. \n \b Please note, a signature is still required.'
                }
                submittext={'Continue'}
                hideCancelButton={true}
                onClose={() => setShowConfirmDialog(false)}
                onSubmit={handleContinue}
              />
            </Box>
            <Typography sx={{ fontSize: '20px', textAlign: 'left', marginTop: 3 }}>
              If you have any questions or concerns regarding your session note review, please
              contact your BCBA or the QA team{' '}
              <span
                onClick={() => (window.location.href = 'mailto:qa@fortahealth.com')}
                style={{
                  color: FORTA_BLUE.primary,
                  fontSize: '20px',
                  textDecoration: 'underline',
                  cursor: 'pointer',
                }}
              >
                qa@fortahealth.com
              </span>
            </Typography>
            <Box
              sx={{
                width: '100%',
                height: '2px',
                marginTop: 3,
                backgroundColor: FORTA_GRAY4,
              }}
            />
            <Grid
              container
              sx={{
                display: 'flex',
                flexDirection: 'row',
                padding: 2,
                alignItems: 'flex-start',
                justifyContent: 'space-between',
                marginTop: 3,
                backgroundColor: FORTA_WHITE,
                borderRadius: '10px',
              }}
            >
              <Grid
                item
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                }}
              >
                <Typography
                  sx={{
                    fontSize: '24px',
                    fontWeight: '700',
                    color: FORTA_BLUE.primary,
                    marginBottom: 3,
                  }}
                >
                  Acknowledge receipt
                </Typography>
                <Typography sx={{ fontSize: '14px', color: FORTA_GRAY4 }}>
                  By clicking submit, I acknowledge receipt of this session notes quality report.
                </Typography>
              </Grid>
              <Grid
                data-testid="acknowledgeSignature"
                item
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                }}
              >
                <Paper
                  sx={{
                    position: 'relative',
                    backgroundColor:
                      !clinical_qa_review?.acknowledged_signature &&
                      areUserRolesIn(user?.roles, [Roles.Prbt, Roles.Vbt])
                        ? FORTA_BLUE.light3
                        : FORTA_WHITE,
                    m: '24px auto 0 auto',
                    height: 180,
                    overflow: 'hidden',
                    borderRadius: '10px',
                    boxShadow:
                      !clinical_qa_review?.acknowledged_signature &&
                      areUserRolesIn(user?.roles, [Roles.Prbt, Roles.Vbt])
                        ? '0px 4px 4px 0px rgba(18, 78, 120, 0.25)'
                        : 'none',
                  }}
                >
                  <Box
                    sx={{
                      width: '100%',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                    }}
                  >
                    <Box>
                      <SignatureCanvas
                        ref={(ref: any) => setSignatureCanvasRef(ref)}
                        canvasProps={{
                          className: 'sigCanvas',
                          width: 570,
                          height: 190,
                          style: { backgroundColor: 'transparent' },
                        }}
                      />
                    </Box>
                  </Box>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      position: 'absolute',
                      bottom: 20,
                      left: 20,
                      width: 'calc(100% - 40px)',
                      height: '2px',
                      backgroundColor: FORTA_BLUE.primary,
                    }}
                  >
                    <IconButton
                      sx={{
                        top: -35,
                        left: -10,
                      }}
                      onClick={handleClear}
                      disabled={
                        !(
                          areUserRolesIn(user?.roles, [Roles.Prbt, Roles.Vbt]) &&
                          !clinical_qa_review?.acknowledged_signature
                        )
                      }
                    >
                      <ClearSignatureIcon
                        color={
                          areUserRolesIn(user?.roles, [Roles.Prbt, Roles.Vbt]) &&
                          !clinical_qa_review?.acknowledged_signature
                            ? FORTA_BLUE.primary
                            : FORTA_BLUE.light2
                        }
                      />
                    </IconButton>
                  </Box>
                </Paper>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignContent: 'flex-start',
                    width: '100%',
                    p: 2,
                  }}
                >
                  <Typography>
                    {session_provider?.first_name} {session_provider?.last_name}
                  </Typography>
                  <Typography>
                    {clinical_qa_review?.date_review_acknowledged &&
                      moment(clinical_qa_review?.date_review_acknowledged).format(
                        'MM/DD/YYYY hh:mm A',
                      )}
                  </Typography>
                </Box>
                {areUserRolesIn(user?.roles, [Roles.Prbt, Roles.Vbt]) &&
                  !clinical_qa_review?.acknowledged_signature && (
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        alignContent: 'flex-start',
                        width: '100%',
                        padding: 2,
                        gap: 3,
                      }}
                    >
                      <FHButton
                        variant="contained"
                        sx={{
                          width: '175px',
                          px: 1,
                          py: 2,
                          backgroundColor: FORTA_WHITE,
                          color: FORTA_GRAY4,
                          border: `1px solid ${FORTA_BLUE.light2}`,
                          boxShadow: '0px 4px 4px 0px rgba(65, 113, 147, 0.25)',
                          '&:hover': {
                            backgroundColor: FORTA_WHITE,
                            color: FORTA_BLUE.primary,
                          },
                        }}
                        onClick={handleClear}
                      >
                        Clear Signature {!clinical_qa_review?.acknowledged_signature}
                      </FHButton>
                      <FHButton
                        data-testid="submitSignature"
                        variant="contained"
                        sx={{
                          width: '90px',
                          px: 1,
                          py: 2,
                        }}
                        onClick={handleAcknowledgement}
                      >
                        Submit
                      </FHButton>
                    </Box>
                  )}
              </Grid>
            </Grid>
            {downloadAllowed && (
              <Box
                sx={{
                  display: 'flex',
                  alignContent: 'flex-start',
                  width: '100%',
                  marginTop: 3,
                }}
              >
                <FHButton
                  variant="contained"
                  sx={{
                    width: '175px',
                    px: 1,
                    py: 2,
                    backgroundColor: FORTA_BLUE.primary,
                    color: FORTA_WHITE,
                    border: `1px solid ${FORTA_BLUE.light2}`,
                    boxShadow: '0px 4px 4px 0px rgba(65, 113, 147, 0.25)',
                  }}
                  onClick={() => setOpenConfirmDownload(true)}
                >
                  Download PDF
                </FHButton>
              </Box>
            )}
          </>
        )}
      </Box>

      {!isAdminView && (
        <FHConfirmDialog
          open={openConfirmDownload}
          content={'Are you sure you want to download this session note review ?'}
          submittext={'Download'}
          canceltext={'Back to session note review'}
          onClose={() => setOpenConfirmDownload(false)}
          onSubmit={() => {
            handleSessionNoteReviewDownload();
          }}
        />
      )}
    </PageContainer>
  );
}

export default SessionReview;
